import {isLoggedIn} from '@github-ui/client-env'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useNavigate} from '@github-ui/use-navigate'
import {PlusIcon} from '@primer/octicons-react'
import {Button, type ButtonProps, IconButton, type IconButtonProps} from '@primer/react'
import {useCallback, useMemo, useRef, useState} from 'react'

import {CreateIssueButtonLoading} from './CreateIssueButtonLoading'
import type {CreateIssueDialogEntryProps} from './dialog/CreateIssueDialogEntry'
import {CreateIssueDialogEntry} from './dialog/CreateIssueDialogEntry'
import {loginPath} from './utils/urls'

type CreateIssueButtonBaseProps = {
  label: string
  size?: 'small' | 'medium'
  isDialogOpenByDefault?: boolean
  variant?: ButtonProps['variant']
} & Omit<CreateIssueDialogEntryProps, 'setIsCreateDialogOpen' | 'isCreateDialogOpen'>

export type CreateIssueButtonProps = CreateIssueButtonBaseProps &
  (
    | {
        /** When true, render as an IconButton (icon-only). The `label` is used as the aria-label. Defaults to PlusIcon when `leadingVisual` is not provided. */
        iconOnly: true
        leadingVisual?: ButtonProps['leadingVisual']
      }
    | {
        /** When false or omitted, render as a text Button. `leadingVisual` is used as a leading visual icon alongside the button label. */
        iconOnly?: false
        leadingVisual?: ButtonProps['leadingVisual']
      }
  )

export const CreateIssueButton = ({
  label,
  size = 'medium',
  variant = 'primary',
  leadingVisual,
  iconOnly = false,
  ...props
}: CreateIssueButtonProps): React.ReactElement | null => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isNavigatingToNew, setIsNavigatingToNew] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const isViewerLoggedIn = isLoggedIn()

  const openDialog = useCallback(() => {
    if (isViewerLoggedIn) {
      setIsCreateDialogOpen(true)
    } else {
      navigate(loginPath())
    }
  }, [isViewerLoggedIn, navigate])

  const onCreateIssueShortcutClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      openDialog()
    },
    [openDialog],
  )

  const renderedButton = useMemo(() => {
    if (isNavigatingToNew) {
      return <CreateIssueButtonLoading label={label} size={size} iconOnly={iconOnly} leadingVisual={leadingVisual} />
    }
    return (
      <>
        {isViewerLoggedIn && <GlobalCommands commands={{'issue-create:new': openDialog}} />}
        <RenderedCreateButton
          ref={buttonRef}
          size={size}
          label={label}
          variant={variant}
          leadingVisual={leadingVisual}
          iconOnly={iconOnly}
          onClick={onCreateIssueShortcutClick}
          optionConfig={props.optionConfig}
        />
      </>
    )
  }, [
    isNavigatingToNew,
    props.optionConfig,
    isViewerLoggedIn,
    openDialog,
    size,
    label,
    variant,
    leadingVisual,
    iconOnly,
    onCreateIssueShortcutClick,
  ])

  if (!isCreateDialogOpen) return renderedButton

  const dialogProps = {
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    setIsNavigatingToNew,
    canBypassTemplateSelection: true,
    isNavigatingToNew,
    returnFocusRef: buttonRef,
    fallbackElement: (
      <CreateIssueButtonLoading label={label} size={size} iconOnly={iconOnly} leadingVisual={leadingVisual} />
    ),
    ...props,
  }

  return <CreateIssueDialogEntry {...dialogProps} />
}

type RenderedCreateButtonProps = {
  label: string
  size?: 'small' | 'medium'
  variant?: ButtonProps['variant']
  leadingVisual?: ButtonProps['leadingVisual']
  iconOnly?: boolean
  onClick: (e: React.MouseEvent) => void
  ref?: React.Ref<HTMLButtonElement>
} & Pick<CreateIssueButtonBaseProps, 'optionConfig'>

const RenderedCreateButton = ({
  ref,
  size,
  label,
  variant = 'primary',
  leadingVisual,
  iconOnly = false,
  onClick,
  optionConfig,
}: RenderedCreateButtonProps) => {
  const buttonHrefProps = useMemo(() => {
    if (!isLoggedIn()) {
      return {
        as: 'a',
        href: loginPath(),
        target: '_blank',
      } as Partial<ButtonProps>
    } else if (optionConfig?.issueCreateArguments?.repository) {
      const {owner, name} = optionConfig.issueCreateArguments.repository
      return {
        as: 'a',
        href: `/${owner}/${name}/issues/new/choose`,
        target: '_blank',
      } as Partial<ButtonProps>
    } else {
      // on issue#dashboard there is no href - when we click on the button it's a no-op
      // until React is fully loaded and we can render the issue create dialog
      return {}
    }
  }, [optionConfig?.issueCreateArguments?.repository])

  // we want to default to the underlying anchor functionality when the user is holding down the cmd or ctrl key
  // and therefore ignore the custom onClick functionality - expect on issue#dashboard where we always want to
  // open the dialog
  const ignoreOnClickIfCmdOrCtrlPressed = (e: React.MouseEvent, clickHandler: (e: React.MouseEvent) => void) => {
    const isIssueDashboard = !optionConfig?.issueCreateArguments?.repository
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if ((!e.ctrlKey && !e.metaKey) || isIssueDashboard) {
      clickHandler(e)
    }

    // ..bubble down to the underlying anchor functionality
  }

  if (iconOnly) {
    const icon = (leadingVisual ?? PlusIcon) as IconButtonProps['icon']
    const hrefProps = buttonHrefProps as {as?: 'a' | 'button'; href?: string; target?: string}
    return (
      <IconButton
        ref={ref}
        size={size}
        variant={variant}
        icon={icon}
        aria-label={label}
        onClick={(e: React.MouseEvent) => ignoreOnClickIfCmdOrCtrlPressed(e, onClick)}
        {...hrefProps}
      />
    )
  }

  return (
    <Button
      ref={ref}
      size={size}
      variant={variant}
      leadingVisual={leadingVisual}
      onClick={(e: React.MouseEvent) => ignoreOnClickIfCmdOrCtrlPressed(e, onClick)}
      {...buttonHrefProps}
    >
      {label}
    </Button>
  )
}

RenderedCreateButton.displayName = 'RenderedCreateButton'
